/**
 * Navigation state store.
 *
 * Backs the subscribe/getSnapshot contract exposed via
 * Drupal.sdxNavigation. Framework hooks (React's
 * useSyncExternalStore, Vue's shallowRef, Svelte's readable
 * stores) plug into it directly instead of polling DOM state.
 *
 * Extends @sdx/core/observable's ObservableStore so the
 * subscribe + apply + notify primitives are shared with every
 * other SDX state store. The class exposes semantic state
 * transitions (setLoading, setIdle, setError) on top of the
 * base `apply` so callers do not construct raw patches at every
 * call site.
 *
 * Note: this store exposes its current snapshot via a
 * `getState()` alias for backward compatibility with the
 * public Drupal.sdxNavigation contract and with framework
 * hooks that documented the old name. New callers should
 * prefer `getSnapshot()` from the base class for consistency
 * across every SDX core store.
 */

import { ObservableStore } from '@sdx/core/observable';
import type { NavScope, NavState, PageData } from './types';

export class NavStateStore extends ObservableStore<NavState> {
  constructor() {
    super({
      status: 'idle',
      url: location.href,
      path: location.pathname,
      scope: 'full',
      pageData: null,
      error: null,
    });
  }

  /** Backward-compat alias for the public Drupal.sdxNavigation API. */
  getState = (): Readonly<NavState> => this.getSnapshot();

  isNavigating(): boolean {
    return this.getSnapshot().status === 'loading';
  }

  setLoading(url: string, path: string, scope: NavScope): void {
    this.apply({ status: 'loading', url, path, scope, error: null });
  }

  setIdle(pageData: PageData | null, url: string, path: string): void {
    this.apply({ status: 'idle', pageData, url, path, error: null });
  }

  setError(error: string): void {
    this.apply({ status: 'error', error });
  }
}
